import { _Linkable } from 'prismic-types';
import { Link } from '../link/Link';
import s from './MegaMenu.module.scss';
import { MotionProps, motion } from 'framer-motion';
import { linkResolver } from 'prismic/linkResolver';
import { v4 as uuidv4 } from 'uuid';
import classNames from 'classnames/bind';
import { useUiState } from 'hooks/useUiState';

const c = classNames.bind(s);

export type MegaMenuGroupProps = {
  title: string;
  links: Array<{
    title: string;
    link: _Linkable;
  }>;
};

const group = {
  variants: {
    closed: { y: -16, opacity: 0 },
    open: { y: 0, opacity: 1 },
  },
  transition: { opacity: { duration: 0.2 }, y: { duration: 0.2 } },
} satisfies MotionProps;

export const MegaMenuGroup = ({ title, links }: MegaMenuGroupProps) => {
  const { uiState } = useUiState();
  const { currentPage } = uiState;
  const uid = uuidv4();
  const firstLink =
    links && links[0] && links[0].link && linkResolver(links[0].link);

  return (
    <motion.div
      className={s.group}
      variants={group.variants}
      initial="closed"
      key={`${title}-${uid}`}
    >
      {firstLink && (
        <Link className={s.group__title} to={firstLink}>
          {title}
        </Link>
      )}
      <ul>
        {links.map((link) => (
          <MegaMenuLinkItem
            key={`${link.title}-${uid}`}
            title={link.title}
            link={link.link}
            isActive={linkResolver(link.link) === currentPage}
          />
        ))}
      </ul>
    </motion.div>
  );
};

const MegaMenuLinkItem = ({
  title,
  link,
  isActive,
}: {
  title: string;
  link: _Linkable;
  isActive: boolean;
}) => {
  const url = linkResolver(link);
  return (
    <li className={c(s.linkItem, { isActive })}>
      <svg
        className={s.dot}
        width="12"
        height="22"
        viewBox="0 0 12 22"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M4.625 22L5.3125 12.925L0.749791 11.6214C0.623599 11.5853 0.565749 11.4395 0.632877 11.3268L7.375 0L6.6875 9.075L11.2502 10.3786C11.3764 10.4147 11.4343 10.5605 11.3671 10.6732L4.625 22Z"
          fill="url(#paint0_linear_7322_39481)"
        />
        <path
          d="M4.625 22L5.3125 12.925L0.749791 11.6214C0.623599 11.5853 0.565749 11.4395 0.632877 11.3268L7.375 0L6.6875 9.075L11.2502 10.3786C11.3764 10.4147 11.4343 10.5605 11.3671 10.6732L4.625 22Z"
          fill="url(#paint1_radial_7322_39481)"
        />
        <path
          d="M4.625 22L5.3125 12.925L0.749791 11.6214C0.623599 11.5853 0.565749 11.4395 0.632877 11.3268L7.375 0L6.6875 9.075L11.2502 10.3786C11.3764 10.4147 11.4343 10.5605 11.3671 10.6732L4.625 22Z"
          fill="url(#paint2_linear_7322_39481)"
          fillOpacity="0.2"
        />
        <defs>
          <linearGradient
            id="paint0_linear_7322_39481"
            x1="11.1792"
            y1="-1.81171"
            x2="-1.19423"
            y2="-1.04658"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#320564" />
            <stop offset="1" stopColor="#5FFFC2" />
          </linearGradient>
          <radialGradient
            id="paint1_radial_7322_39481"
            cx="0"
            cy="0"
            r="1"
            gradientUnits="userSpaceOnUse"
            gradientTransform="translate(5.7293 -2.17693) rotate(101.45) scale(39.4657 22.3172)"
          >
            <stop stopColor="#7838CA" />
            <stop offset="0.0001" stopColor="#A159FE" stopOpacity="0.62" />
            <stop offset="0.805309" stopColor="#2B0857" stopOpacity="0" />
          </radialGradient>
          <linearGradient
            id="paint2_linear_7322_39481"
            x1="11.5"
            y1="22"
            x2="-2.95242"
            y2="19.4323"
            gradientUnits="userSpaceOnUse"
          >
            <stop />
            <stop offset="1" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>

      <Link to={url}>{title}</Link>
    </li>
  );
};
